import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Activity, AlertTriangle, Briefcase, Compass, Loader2, Plus, ShieldCheck, Trash2, TrendingUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, patch } from "../../lib/api";
import { Card } from "../ui";
import { ReviewCard } from "./Review";
import {
  BAND_LABELS, CATEGORIES, CHECKLIST, eur, eurShort, errText, Flag, Kpi, Pill, Row, WK,
  type PjEvent, type ProjectionSettings, type Strategy, type WealthDoc, type WealthSettings,
} from "./shared";

export function useStrategy(shocks: string[]) {
  return useQuery({
    queryKey: [...WK.strategy, shocks.join(",")],
    queryFn: () => api<Strategy>(`/api/wealth/strategy${shocks.length ? `?shocks=${shocks.join(",")}` : ""}`),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
}

// Ten-year projection: buckets (chart order, bottom to top), shock switches and the assumption fields — every one is a setting, none is a forecast
const PJ_BUCKETS: Array<[string, string, string]> = [["property", "Property", "#0b0c2a"], ["companies", "Companies", "#2b35ff"], ["investment", "Investments", "#9db4f5"], ["cash", "Cash", "#10b981"], ["pension", "Pension", "#f59e0b"]];
const PJ_SHOCKS: Record<string, string> = { property20: "Property −20% now", profitHalf: "Profit halves for 2 years", noGrowth: "No growth at all", clientLoss: "Lose the largest client" };
const PJ_FIELDS: Array<[string, string]> = [["profitGrowthPct", "Company profit growth"], ["propertyGrowthPct", "Property growth"], ["rentGrowthPct", "Rent growth"], ["investmentReturnPct", "Investment return"], ["cashInterestPct", "Interest on cash"], ["pensionReturnPct", "Pension return"], ["inflationPct", "Inflation on living costs"], ["rentTaxPct", "Tax on rent"]];
const PJ_PLACES: Array<[string, string]> = [["outside", "outside (new money / spent)"], ["cash", "cash"], ["investment", "investments"], ["property", "property"], ["pension", "pension"]];
const STATUS_TEXT: Record<string, string> = { in: "in band", above: "above band", below: "below band", unvalued: "not valued" };
const RED = "#b91c1c", GREEN = "#065f46";

export function StrategyTab({ isPhone, openReview }: { isPhone: boolean; openReview: boolean }) {
  const qc = useQueryClient();
  const [shocks, setShocks] = useState<string[]>([]);
  const q = useStrategy(shocks);
  const sg = q.data;
  const toggleShock = (k: string) => setShocks((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const saveSettings = async (body: Partial<WealthSettings>) => {
    const r = await patch<{ ok: boolean; settings: WealthSettings; updatedAt: string }>("/api/wealth/settings", body);
    qc.setQueryData<WealthDoc>(WK.doc, (d) => (d ? { ...d, settings: r.settings } : d));
    return r;
  };

  // Assumptions and planned events save 800 ms after the last keystroke. Edits ACCUMULATE until the save fires:
  // a second edit inside the 800 ms must not drop the first one
  const [pjDraft, setPjDraft] = useState<ProjectionSettings | null>(null);
  const [pjErr, setPjErr] = useState("");
  const pjTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pjPending = useRef<ProjectionSettings>({});
  useEffect(() => () => clearTimeout(pjTimer.current), []);
  const patchProjection = (p: ProjectionSettings) => {
    setPjDraft((d) => ({ ...(d || sg?.projection?.assumptions || {}), ...p }));
    pjPending.current = { ...pjPending.current, ...p };
    clearTimeout(pjTimer.current);
    pjTimer.current = setTimeout(async () => {
      const send = pjPending.current;
      pjPending.current = {};
      try {
        await saveSettings({ projection: send });
        await qc.invalidateQueries({ queryKey: WK.strategy });
        if (!Object.keys(pjPending.current).length) setPjDraft(null);
        setPjErr("");
      } catch (e) {
        setPjErr(errText(e));
      }
    }, 800);
  };

  const toggleCheck = async (k: string, v: boolean) => {
    if (!sg) return;
    const checklist = { ...Object.fromEntries(sg.succession.checklist.map((c) => [c.key, c.done])), [k]: v };
    qc.setQueriesData<Strategy>({ queryKey: WK.strategy }, (s) => {
      if (!s) return s;
      const list = s.succession.checklist.map((c) => (c.key === k ? { ...c, done: v } : c));
      return { ...s, succession: { ...s.succession, checklist: list, done: list.filter((c) => c.done).length } };
    });
    try {
      await saveSettings({ checklist });
      void qc.invalidateQueries({ queryKey: WK.review });
    } catch (e) {
      window.alert(`Could not save: ${errText(e)}`);
    }
  };

  if (q.error && !sg) return <p className="muted text-sm">The strategy figures are not available right now ({errText(q.error)}).</p>;
  if (!sg) return <p className="muted text-sm"><Loader2 size={13} className="mr-1 inline animate-spin" /> Working it out…</p>;

  const breachList = sg.policy.filter((p) => p.status === "above" || p.status === "below").map((p) => BAND_LABELS[p.key]!.toLowerCase()).join(", ");
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Net worth incl. companies" icon={<Compass size={18} />} value={eurShort(sg.full)} sub={sg.companiesValue ? `companies ${eurShort(sg.companiesValue)} + assets ${eurShort(sg.totals.total)}` : "companies not valued yet — see below"} />
        <Kpi label="Liquid within 30 days" value={`${sg.liquidPct}%`} sub={`${eurShort(sg.liquid)}${sg.income.yearsCovered != null ? ` · ${sg.income.yearsCovered} years of living costs` : ""}`} />
        <Kpi
          label="Passive income cover"
          value={sg.income.cover != null ? `${sg.income.cover}×` : "—"}
          sub={sg.income.living ? `${eurShort(sg.income.year)}/yr from assets ÷ ${eurShort(sg.income.living)} living · ${sg.income.coverWithDividend}× with the dividend` : "set living costs in Wealth settings"}
        />
        <Kpi label="Policy breaches" value={sg.breaches} tone={sg.breaches ? "bad" : "good"} sub={breachList || "everything inside its band"} />
      </div>

      <ReviewCard openInitially={openReview} saveSettings={saveSettings} />

      <Card title="Policy bands vs actual" icon={<Compass size={16} />}>
        <p className="muted -mt-1 mb-3 text-xs">Green = your band · purple = today · % of net worth including the companies · edit the bands in Wealth settings.</p>
        {sg.policy.map((p) => (
          <div key={p.key} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 py-1.5 text-sm sm:grid-cols-[150px_minmax(0,1fr)_200px]">
            <span className="truncate">{BAND_LABELS[p.key]}</span>
            <span className={clsx("text-right text-xs font-semibold num sm:order-last", p.status === "in" ? "text-emerald-700" : p.status === "unvalued" ? "muted" : "text-red-700")}>
              {p.actual}% · {STATUS_TEXT[p.status]}{p.gap ? ` (${eurShort(p.gap)})` : ""}
            </span>
            <div className="relative col-span-2 h-3 overflow-hidden rounded-full bg-[var(--panel-2)] ring-1 ring-[var(--line)] sm:col-span-1">
              <div className="absolute inset-y-0 bg-emerald-200/80" style={{ left: `${p.lo}%`, width: `${Math.max(0.5, p.hi - p.lo)}%` }} />
              <div className="absolute inset-y-[3px] left-0 rounded-full bg-violet-600/80" style={{ width: `${Math.min(100, p.actual)}%` }} />
            </div>
          </div>
        ))}
        <p className="muted mt-2 text-xs">
          Band {sg.policy.map((p) => `${BAND_LABELS[p.key]!.toLowerCase()} ${p.lo}–${p.hi}%`).join(" · ")}. The bands are yours: they say where you want to be, the bars say where you are. Nothing here tells you what to buy or sell.
        </p>
      </Card>

      {sg.projection && <ProjectionCard sg={sg} draft={pjDraft} shocks={shocks} toggleShock={toggleShock} clearShocks={() => setShocks([])} patchProjection={patchProjection} isPhone={isPhone} error={pjErr} />}

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="The companies, valued" icon={<Briefcase size={16} />}>
          <p className="muted -mt-1 mb-2 text-xs">{sg.assumptions.multiple}× average profit (CRM, last 3 years) — a modelling assumption, not a valuation.</p>
          {!sg.companies.length && <p className="muted text-sm">No companies listed — add them in Wealth settings.</p>}
          {sg.companies.map((c) => (
            <div key={c.id || c.name} className="border-t border-[var(--line)] py-2">
              <Row label={<span className="font-semibold">{c.name}</span>} value={c.basis === "none" ? <span className="muted">not valued</span> : eur(c.value)} strong />
              {c.basis !== "none" && (
                <Row muted label={c.basis === "own" ? "Your own figure" : `${c.multiple}× average profit ${eurShort(c.avgProfit)}`} value={c.basis === "own" ? `multiple method: ${eurShort(c.multipleValue)}` : `range ${eurShort(c.low)}–${eurShort(c.high)}`} />
              )}
              {c.years > 0 && <Row muted label="Profit by year (CRM)" value={Object.entries(c.profits).map(([y, v]) => `${y} ${eurShort(v)}`).join(" · ")} />}
              <Row muted label="Cash in its accounts" value={<>{eur(c.cash)} <span title="Already counted under Cash — not added twice">(in Cash)</span></>} />
              {c.basis === "none" && <p className="muted mt-1 text-xs">No CRM profit matched "{c.name}" — name it IFT or IFI, or enter your own value in Wealth settings.</p>}
            </div>
          ))}
          {Object.keys(sg.unassigned || {}).length > 0 && (
            <Flag pill="unassigned">
              Profit with no company set in the CRM is in no valuation: {Object.entries(sg.unassigned).map(([y, v]) => `${y} ${eurShort(v)}`).join(" · ")}. Fill in the company on those project rows in the CRM and it counts.
            </Flag>
          )}
          <p className="muted mt-2 text-xs">Change the multiple or enter your own figure in Wealth settings. The value is the earnings power on top of the cash already listed.</p>
        </Card>

        <Card title="Concentration" icon={<AlertTriangle size={16} className="text-amber-600" />} flush>
          <div className="overflow-x-auto">
            <table className="table">
              <thead><tr><th>Exposure</th><th className="!text-right">Share</th><th>Flag</th></tr></thead>
              <tbody>
                {sg.concentration.map((c) => (
                  <tr key={c.key}>
                    <td>{c.label}<div className="muted text-[11.5px]">{c.note}</div></td>
                    <td className="text-right num">{c.share != null ? `${c.share}%` : "—"}</td>
                    <td><Pill tone={c.level}>{c.level === "high" ? "high" : c.level === "watch" ? "watch" : c.level === "unknown" ? "needs data" : "ok"}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card title="Stress tests" icon={<Activity size={16} />}>
        <p className="muted -mt-1 mb-2 text-xs">Mechanical, from today's figures. "Years covered" = liquid reserves ÷ living costs after the hit.</p>
        <div className="overflow-x-auto">
          <table className="table min-w-[680px]">
            <thead><tr><th>Scenario</th><th className="!text-right">Net worth</th><th className="!text-right">Liquidity</th><th className="!text-right">Years covered after</th><th>Note</th></tr></thead>
            <tbody>
              {sg.stress.map((x) => (
                <tr key={x.key}>
                  <td>{x.label}</td>
                  <td className="text-right num" style={{ color: x.netWorth < 0 ? RED : undefined }}>{x.netWorth ? `${eurShort(x.netWorth)} (${x.netWorthPct}%)` : "±0"}</td>
                  <td className="text-right num" style={{ color: x.liquidity < 0 ? RED : undefined }}>{x.liquidity ? eurShort(x.liquidity) : "±0"}</td>
                  <td className="text-right num">{x.yearsAfter != null ? x.yearsAfter : "—"}</td>
                  <td className="muted text-xs">{x.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <IncomeCard sg={sg} />
        <Card title="Protection and succession" icon={<ShieldCheck size={16} />}>
          <Row label="Duty on property at succession" value={`≈ ${eur(sg.succession.duty)}`} strong />
          <p className="muted mb-2 mt-0.5 text-xs">
            5% of {eurShort(sg.totals.by.property ?? 0)} — the upper figure (3.5% applies to the first €400k of a residence). Malta has no general inheritance tax. For your notary to confirm; shown here as a liquidity item
            {sg.succession.dutyPctOfLiquid != null ? ` (${sg.succession.dutyPctOfLiquid}% of what is liquid today)` : ""}.
          </p>
          {CHECKLIST.map(([k, label]) => {
            const done = !!sg.succession.checklist.find((c) => c.key === k)?.done;
            return (
              <label key={k} className="flex cursor-pointer items-start gap-2.5 py-1 text-sm">
                <input type="checkbox" className="mt-0.5 shrink-0" checked={done} onChange={(e) => void toggleCheck(k, e.target.checked)} />
                <span className={clsx(done && "muted line-through")}>{label}</span>
              </label>
            );
          })}
          <p className="muted mt-2 text-xs">{sg.succession.done}/{sg.succession.of} done.</p>
        </Card>
      </div>
    </div>
  );
}

function IncomeCard({ sg }: { sg: Strategy }) {
  const r = sg.rentals;
  return (
    <Card title="What the assets pay" icon={<TrendingUp size={16} />}>
      <Row label="From assets, per year" value={eur(sg.income.year)} strong />
      {Object.entries(sg.income.byCategory).map(([k, v]) => <Row key={k} indent label={CATEGORIES.find((c) => c.k === k)?.label || k} value={eur(v)} muted />)}
      {r?.ok && (
        <>
          <Row indent muted label={`short lets, last ${r.completedMonths} months`} value={`${r.bookings12} stays · ${r.nights12} nights`} />
          <Row indent muted label="of which from the rentals app, net" value={eur(sg.income.shortLetYear)} />
          <Row indent muted label="booked ahead, short lets" value={eur(r.bookedAhead ?? 0)} />
          {r.other && (r.other.leaseLines > 0 || r.other.events > 0) && (
            <>
              <Row indent muted label="leases and events received, 12 months" value={eur(r.other.received12)} />
              <Row indent muted label="leases and events still expected" value={<>{eur(r.other.expected)}{r.other.overdue > 0 && <span className="text-red-700"> · {eur(r.other.overdue)} overdue</span>}</>} />
            </>
          )}
          {r.linked.map((l) => (
            <Row
              key={l.id} indent muted label={l.name}
              value={l.noStays ? `nothing in the rentals app yet · typed ${eur(l.monthly * 12)}` : `${l.stays ? `${eur(l.net12)} · ${l.priced} of ${l.stays} priced · ${l.nights} nights` : ""}${l.leaseLines || l.events ? `${l.stays ? " · " : ""}leases/events ${eur(l.otherReceived12)} received, ${eur(l.otherExpected)} expected` : ""}`}
            />
          ))}
          {!r.complete && (
            <Flag pill="amounts">{r.priced12} of {(r.priced12 ?? 0) + (r.unpriced12 ?? 0)} stays have an amount in the rentals app, so these figures are lower than what was earned. They rise by themselves as amounts are entered there.</Flag>
          )}
          {r.unlinked.length > 0 && (
            <Flag pill="link">
              {r.unlinked.join(", ")} earn{r.unlinked.length === 1 ? "s" : ""} in the rentals app but {r.unlinked.length === 1 ? "is" : "are"} not linked to an asset — pick "short let: …" in the Kind column of the property row in Assets.
            </Flag>
          )}
        </>
      )}
      {r && !r.ok && <Row indent muted label="short lets (rentals app)" value="not available right now" />}
      <Row label="This year's dividend, net" value={sg.income.crm ? eur(sg.income.dividendNet) : <span className="muted">CRM not available right now</span>} />
      <Row label="Living costs" value={sg.income.living ? eur(sg.income.living) : <span className="muted">not set</span>} />
      <p className="muted mt-2 text-xs">
        {sg.income.propertiesWithoutIncome
          ? `${sg.income.propertiesWithoutIncome} propert${sg.income.propertiesWithoutIncome === 1 ? "y has" : "ies have"} no income entered — fill "Pays / month" in Assets (0 for the ones you use yourself). Short lets: pick the unit in the Kind column and the figure follows the rentals app.`
          : "Every property has an income figure."}
      </p>
    </Card>
  );
}

function ProjectionCard({ sg, draft, shocks, toggleShock, clearShocks, patchProjection, isPhone, error }: {
  sg: Strategy; draft: ProjectionSettings | null; shocks: string[]; toggleShock: (k: string) => void; clearShocks: () => void;
  patchProjection: (p: ProjectionSettings) => void; isPhone: boolean; error: string;
}) {
  const pj = sg.projection!, h = pj.headline, A = draft || pj.assumptions;
  const max = Math.max(...pj.rows.map((r) => r.full), 1);
  const events: PjEvent[] = A.events || [];
  const setEvent = (i: number, p: Partial<PjEvent>) => patchProjection({ events: events.map((e, j) => (j === i ? { ...e, ...p } : e)) });
  const val = (k: string) => { const v = A[k]; return typeof v === "number" || typeof v === "string" ? v : ""; };
  return (
    <Card title={`${A.years}-year projection`} icon={<TrendingUp size={16} />}>
      <p className="muted -mt-1 mb-3 text-xs">
        Arithmetic on your own assumptions, not a forecast: each December the companies' profit comes out as a dividend, rents and interest come in, living costs go out, and the rest lands in {A.surplusTo === "investment" ? "investments" : "cash"}.
      </p>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label={`Net worth in ${h.endYear}`} value={eurShort(h.endFull)} sub={`from ${eurShort(h.startFull)} at end ${h.startYear} · ${h.growth >= 0 ? "+" : ""}${eurShort(h.growth)}`} />
        <Kpi label={`Liquid in ${h.endYear}`} value={eurShort(h.endLiquid)} sub={h.lowestCover ? `lowest point ${h.lowestCover.years} years of living costs (${h.lowestCover.year})` : "set living costs"} />
        <Kpi label="Living costs covered without the companies" value={h.independenceYear ? `from ${h.independenceYear}` : "not in this period"} sub={`rents + assumed returns ÷ living costs · ${h.endCover}× in ${h.endYear}`} />
        <Kpi label="Years outside your bands" value={`${h.breachYears} of ${pj.rows.length}`} tone={h.breachYears ? "bad" : "good"} sub={h.shortfallYears.length ? `money runs short in ${h.shortfallYears.join(", ")}` : "no year runs short of money"} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-1.5">
        <span className="muted mr-1 text-xs">What if:</span>
        {pj.shocksAvailable.map((k) => (
          <button key={k} className={clsx("chip cursor-pointer", shocks.includes(k) && "!border-brand-600 !bg-brand-600 !text-white")} onClick={() => toggleShock(k)}>
            {PJ_SHOCKS[k] ?? k}{k === "clientLoss" && pj.topClient ? ` (${pj.topClient})` : ""}
          </button>
        ))}
        {shocks.length > 0 && <button className="chip cursor-pointer" onClick={clearShocks}>Clear</button>}
      </div>

      <div className="mt-4 flex h-52 items-end gap-1 sm:gap-1.5" role="img" aria-label={`Net worth by year from ${h.startYear} to ${h.endYear}`}>
        {pj.rows.map((r) => (
          <div key={r.year} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1" title={`${r.label}: ${eur(r.full)}`}>
            <span className="muted truncate text-[10px] num">{eurShort(r.full)}</span>
            <div className="flex w-full max-w-[38px] flex-col overflow-hidden rounded-t" style={{ height: `${Math.max(4, (r.full / max) * 100)}%` }}>
              {[...PJ_BUCKETS].reverse().map(([k, , c]) => (Number(r[k]) || 0) > 0 && <div key={k} style={{ flex: Number(r[k]), background: c }} />)}
            </div>
            <span className="muted text-[10.5px] num">{isPhone ? String(r.year).slice(2) : r.year}{r.events.length ? " •" : ""}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
        {PJ_BUCKETS.map(([k, l, c]) => <span key={k} className="inline-flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: c }} />{l}</span>)}
        <span className="muted">• = a planned event that year</span>
      </div>

      <div className="-mx-5 mt-4 overflow-x-auto">
        <table className="table min-w-[860px]">
          <thead><tr><th>Year</th><th className="!text-right">Net worth</th><th className="!text-right">Liquid</th><th className="!text-right">Dividend, net</th><th className="!text-right">Rents, net</th><th className="!text-right">Living</th><th className="!text-right">Surplus</th><th className="!text-right">Cover</th><th>Outside band</th></tr></thead>
          <tbody>
            {pj.rows.map((r) => (
              <tr key={r.year}>
                <td>
                  {r.label}
                  {r.events.map((e, i) => <div key={i} className="muted text-[11.5px]">• {e.label}: {eurShort(e.amount)} {e.from} → {e.to}</div>)}
                  {r.signed != null && <div className="muted text-[11.5px]">already signed in the CRM: {eurShort(r.signed)} profit</div>}
                </td>
                <td className="text-right font-semibold num">{eurShort(r.full)}</td>
                <td className="text-right num">{eurShort(r.liquid)}</td>
                <td className="text-right num">{eurShort(r.dividend)}</td>
                <td className="text-right num">{eurShort(r.rents)}</td>
                <td className="text-right num">{eurShort(r.living)}</td>
                <td className="text-right num" style={{ color: r.surplus < 0 ? RED : undefined }}>{eurShort(r.surplus)}{r.shortfall > 0 ? ` (short ${eurShort(r.shortfall)})` : ""}</td>
                <td className="text-right num">{r.cover != null ? `${r.cover}×` : "—"}</td>
                <td className="muted text-xs">{r.breaches.map((k) => BAND_LABELS[k]?.toLowerCase() ?? k).join(", ") || <span style={{ color: GREEN }}>all inside</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4 className="mb-2 mt-5 text-sm font-bold">Your assumptions <span className="muted ml-1 text-xs font-normal">placeholders — change any of them and the projection redraws</span></h4>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {PJ_FIELDS.map(([k, l]) => (
          <label key={k} className="flex items-center justify-between gap-2 text-sm">
            <span className="text-[var(--text-2)]">{l}</span>
            <span className="flex items-center gap-1"><input className="input !w-20 !min-h-0 !py-1 text-right" type="number" step="0.5" value={val(k)} aria-label={l} onChange={(e) => patchProjection({ [k]: e.target.value })} /> %</span>
          </label>
        ))}
        <label className="flex items-center justify-between gap-2 text-sm">
          <span className="text-[var(--text-2)]">Years shown</span>
          <input className="input !w-20 !min-h-0 !py-1 text-right" type="number" min="3" max="30" step="1" value={A.years ?? 10} aria-label="Years shown" onChange={(e) => patchProjection({ years: e.target.value })} />
        </label>
        <label className="flex items-center justify-between gap-2 text-sm">
          <span className="text-[var(--text-2)]">Surplus goes to</span>
          <select className="input !w-36 !min-h-0 !py-1" value={A.surplusTo || "cash"} aria-label="Surplus goes to" onChange={(e) => patchProjection({ surplusTo: e.target.value })}>
            <option value="cash">cash</option>
            <option value="investment">investments</option>
          </select>
        </label>
      </div>
      <p className="muted mt-2 text-xs">
        Fixed from elsewhere: company profit starts at {eurShort(pj.assumptions.baseProfit)} a year (CRM average {pj.assumptions.baseProfitYears.join("–")}, every company), dividend tax {pj.assumptions.dividendTaxPct}%, living costs {eurShort(pj.assumptions.livingCostsYear)}, rents today {eurShort(pj.assumptions.rentsToday)} a year. Investment returns stay invested.
      </p>

      <h4 className="mb-2 mt-5 text-sm font-bold">Planned events <span className="muted ml-1 text-xs font-normal">a sale, a purchase, a gift, a large expense</span></h4>
      <div className="space-y-2">
        {events.map((e, i) => (
          <div key={e.id || i} className="flex flex-wrap items-center gap-1.5">
            <input className="input !w-[74px] !min-h-0 !py-1" type="number" min={h.startYear} max={h.endYear} value={e.year} aria-label="Year" onChange={(ev) => setEvent(i, { year: Number(ev.target.value) || h.startYear + 1 })} />
            <input className="input !min-h-0 min-w-[140px] flex-1 !py-1" type="text" value={e.label} placeholder="What (e.g. sell Mercury Towers)" aria-label="What" onChange={(ev) => setEvent(i, { label: ev.target.value })} />
            <input className="input !w-[110px] !min-h-0 !py-1 text-right" type="number" min="0" step="10000" value={e.amount} aria-label="Amount" onChange={(ev) => setEvent(i, { amount: Number(ev.target.value) || 0 })} />
            <select className="input !w-auto !min-h-0 !py-1" value={e.from} aria-label="From" onChange={(ev) => setEvent(i, { from: ev.target.value })}>{PJ_PLACES.map(([k, l]) => <option key={k} value={k}>from {l}</option>)}</select>
            <select className="input !w-auto !min-h-0 !py-1" value={e.to} aria-label="To" onChange={(ev) => setEvent(i, { to: ev.target.value })}>{PJ_PLACES.map(([k, l]) => <option key={k} value={k}>to {l}</option>)}</select>
            <button className="btn btn-ghost btn-danger btn-sm !px-1.5" title="Remove" aria-label="Remove" onClick={() => patchProjection({ events: events.filter((_, j) => j !== i) })}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <button className="btn btn-sm mt-2" onClick={() => patchProjection({ events: [...events, { id: crypto.randomUUID(), year: h.startYear + 1, label: "", amount: 100000, from: "cash", to: "property" }] })}>
        <Plus size={14} /> Add an event
      </button>
      {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
    </Card>
  );
}
